import { EventListener, Intersection, Layers, Matrix4 } from 'three';
import { Object3D } from 'three';
import { Plane, Raycaster, Vector3 } from 'three';

import { NormalizedPointer } from '../../math/index.js';
import { BaseControls, DragControlsModifier } from '../index.js';

import { DragControlsEvent, DragControlsEventOptions } from './DragControlsEvent.js';
import { createDragControlsEvent, DragControlsEvents } from './DragControlsEvent.js';

const _plane = new Plane();
const _raycaster = new Raycaster();
_raycaster.firstHitOnly = true;

const _intersections: Intersection[] = [];
const _intersection = new Vector3();

function findGroup(obj: any, group = null) {
  if (obj.isGroup) group = obj;
  if (obj.parent === null) return group;
  return findGroup(obj.parent, group);
}

export type DragControlsEventHandler<TType extends keyof DragControlsEvents> = EventListener<
  DragControlsEvent,
  TType,
  DragControls
>;

/**
 * DragControls class
 *
 * https://github.com/pmndrs/three-stdlib/blob/main/src/controls/DragControls.ts
 */
export class DragControls extends BaseControls<DragControlsEvents> {
  enabled: boolean = true;
  recursive: boolean = true;
  objects: Object3D[] = [];
  group: boolean | Object3D = false;
  plane: Plane = new Plane();
  layers: Layers = new Layers();
  initial: Vector3 = new Vector3();
  offset: Vector3 = new Vector3();
  lastPosition: Vector3 = new Vector3();
  startTime: number = 0;
  lastTimeStamp: number = 0;
  intersection: Intersection = null!;
  controller: AbortController = new AbortController();
  selected: Object3D = null!;
  matrixWorld: Matrix4 | null = null;
  inverseMatrix: Matrix4 = new Matrix4();
  worldPosition: Vector3 = new Vector3();
  position: Vector3 = new Vector3();
  modifiers: DragControlsModifier[] = [];
  floor = true;
  get pointer(): NormalizedPointer {
    if (!this._pointer) {
      throw new Error('DragControls: pointer not set');
    }
    return this._pointer;
  }

  getRaycaster() {
    return _raycaster;
  }

  onDragStart = (evt: PointerEvent) => {
    if (this.enabled === false || !this._pointer) return;

    this._pointer?.updateFromEvent(evt);

    _raycaster.setFromCamera(this._pointer, this._camera);

    _plane.normal.copy(this._camera.up);

    if (!_raycaster.ray.intersectPlane(_plane, _intersection)) {
      return;
    }

    _intersections.length = 0;
    // Then get the first object that intersects with the raycaster with the specified layers
    _raycaster.layers.mask = this.layers.mask;
    _raycaster.intersectObjects(this.objects, this.recursive, _intersections);
    if (_intersections.length > 0) {
      this.intersection = _intersections[0];
      if (this.group) {
        this.selected = this.group === true ? findGroup(this.intersection.object)! : this.group;
      } else {
        this.selected = this.intersection.object;
      }

      this.worldPosition.setFromMatrixPosition(this.selected.matrixWorld);

      if (!this.floor) {
        _plane.setFromNormalAndCoplanarPoint(
          this._camera.getWorldDirection(this.plane.normal),
          this.worldPosition
        );
      }

      if (_raycaster.ray.intersectPlane(_plane, _intersection)) {
        this.startTime = evt.timeStamp;
        this.inverseMatrix.copy(this.matrixWorld ?? this.selected.parent!.matrixWorld).invert();
        this.offset.copy(_intersection).sub(this.worldPosition);
        this.position.copy(_intersection.sub(this.offset)).applyMatrix4(this.inverseMatrix);
        this.initial.copy(this.position);

        this.emitDragEvent(evt, 'dragstart');
        this.attachRestEvents();
      }
    }
  };

  protected onDragMove = (evt: PointerEvent) => {
    if (this.enabled === false) return;

    this.pointer.updateFromEvent(evt);
    _raycaster.setFromCamera(this.pointer, this._camera);

    if (!this.selected || !_raycaster.ray.intersectPlane(_plane, _intersection)) {
      return;
    }

    this.position.copy(_intersection.sub(this.offset)).applyMatrix4(this.inverseMatrix);

    this.emitDragEvent(evt, 'drag');
  };

  protected onDragEnd = (evt: PointerEvent) => {
    if (!this.selected) {
      return;
    }

    this.emitDragEvent(evt, 'dragend');
    this.detachRestEvents();
  };

  protected onDragCancel = (evt: PointerEvent) => {
    if (!this.selected) {
      return;
    }
    this.position.copy(this.initial);
    this.emitDragEvent(evt, 'dragcancel');
    this.detachRestEvents();
  };

  protected emitDragEvent(
    evt: PointerEvent,
    type: 'drag' | 'dragstart' | 'dragend' | 'dragcancel'
  ) {
    const state: DragControlsEventOptions = {
      type,
      event: evt,
      controller: this.controller,
      intersection: this.intersection,
      pointer: this.pointer,
      initial: this.initial,
      position: this.position,
      lastPosition: this.lastPosition,
      startTime: this.startTime,
      lastTimeStamp: this.lastTimeStamp,
    };

    const dragEvent = createDragControlsEvent(
      this.modifiers.length ? this.modifiers.reduce((acc, modifier) => modifier(acc), state) : state
    );

    this.dispatchEvent(dragEvent);

    // Update the last position from the event
    this.lastPosition.copy(dragEvent.position);
    this.lastTimeStamp = evt.timeStamp;
  }

  protected attachRestEvents() {
    if (this._domElement) {
      this._domElement.addEventListener('pointermove', this.onDragMove);
      this._domElement.addEventListener('pointerup', this.onDragEnd);
      this._domElement.addEventListener('pointerleave', this.onDragCancel);
    }
  }

  protected detachRestEvents() {
    if (this._domElement) {
      this._domElement.removeEventListener('pointermove', this.onDragMove);
      this._domElement.removeEventListener('pointerup', this.onDragEnd);
      this._domElement.removeEventListener('pointerleave', this.onDragCancel);
    }
  }

  abort() {
    this.controller.abort();
  }

  connect(domElement: HTMLElement, pointer = new NormalizedPointer(domElement)): void {
    if (this._domElement) {
      this.disconnect();
    }

    this._pointer = pointer;
    this._domElement = domElement;
    this._domElement.addEventListener('pointerdown', this.onDragStart);
  }

  disconnect(): void {
    if (this._domElement) {
      this._domElement.removeEventListener('pointerdown', this.onDragStart);
      this.detachRestEvents();
      this._domElement = null;
    } else {
      console.warn('DragControls: disconnect() without connect()');
    }
  }
}

export default DragControls;
